import { Box, Input, Typography } from '@mui/material';
import { CashToCloseData } from 'hooks/useGetCashToClose/types';
import React, { ReactNode } from 'react';
import { Control, Controller, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useStyles } from '../../styles';
import { CashToCloseEntryRow } from '../../types';
import { MoneyField } from '../MoneyField';
import { SelectYesNo } from '../SelectYesNo';

type RowFieldProps = {
  row: CashToCloseEntryRow,
  control: Control<CashToCloseData, object>
}

type EditableFieldProps = RowFieldProps & {
  watch: UseFormWatch<CashToCloseData>,
  isUpdateLoading: boolean
}

type ChangeFieldsProps = EditableFieldProps & {
  setValue: UseFormSetValue<CashToCloseData>
}

type LabeledFieldProps = {
  label: string,
  groupLabel?: string,
  children: ReactNode
}

const LabeledField = ({ label, groupLabel, children }: LabeledFieldProps) => {
  const classes = useStyles();

  return (
    <Box
      className={classes.mobileField}
      role={groupLabel ? 'group' : undefined}
      aria-label={groupLabel}
    >
      <Typography variant="caption" component="div" className={classes.mobileLabel}>
        {label}
      </Typography>
      {children}
    </Box>
  );
};

export const EstimateAmountField = ({
  row,
  control,
  watch,
  isUpdateLoading
}: EditableFieldProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const label = t('cashToClose:loan-estimate-table-title');

  return (
    <LabeledField label={label}>
      <Box className={classes.mobileControl}>
        <Controller
          control={control}
          name={`${row.fieldName}.estimate_amount`}
          render={({ field }) => (
            <MoneyField
              value={watch(`${row.fieldName}.estimate_amount`)}
              onChange={field.onChange}
              isDisabled={isUpdateLoading}
              ariaLabel={`${row.title} ${label}`}
            />
          )}
        />
      </Box>
    </LabeledField>
  );
};

export const FinalAmountField = ({ row, control }: RowFieldProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const label = t('cashToClose:final');

  return (
    <LabeledField label={label}>
      <Box className={classes.mobileControl}>
        <Controller
          control={control}
          name={`${row.fieldName}.final`}
          render={({ field }) => (
            <MoneyField
              isDisabled
              value={field.value}
              onChange={field.onChange}
              ariaLabel={`${row.title} ${label}`}
            />
          )}
        />
      </Box>
    </LabeledField>
  );
};

export const ChangeFields = ({
  row,
  control,
  watch,
  setValue,
  isUpdateLoading
}: ChangeFieldsProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const label = t('cashToClose:did-this-change');
  const ariaLabel = `${row.title} ${label}`;

  return (
    <LabeledField label={label} groupLabel={ariaLabel}>
      <Box className={classes.mobileChange}>
        <Controller
          control={control}
          name={`${row.fieldName}.changed`}
          render={({ field }) => (
            <>
              <Box className={classes.mobileControl}>
                <SelectYesNo
                  fullWidth
                  value={watch(`${row.fieldName}.changed`) ?? null}
                  onChange={(isEnabled) => {
                    if (!isEnabled) {
                      setValue(`${row.fieldName}.changed_text`, '', { shouldDirty: true });
                    }
                    field.onChange(isEnabled);
                  }}
                  isDisabled={isUpdateLoading}
                />
              </Box>

              <Controller
                control={control}
                name={`${row.fieldName}.changed_text`}
                render={({ field: textField }) => (
                  <Box className={classes.mobileControl}>
                    <Input
                      className={classes.inputClassName}
                      disabled={!field.value || isUpdateLoading}
                      value={watch(`${row.fieldName}.changed_text`)}
                      classes={{ disabled: classes.disabledClass }}
                      inputProps={{ 'aria-label': ariaLabel }}
                      onChange={textField.onChange}
                    />
                  </Box>
                )}
              />
            </>
          )}
        />
      </Box>
    </LabeledField>
  );
};
